// src/pages/AdminPanel/DataUploader.jsx
// Bulk upserts trades, valuations, market data (CSV), SLGS Daily Rates (XML),
// SLGS Historical Rates (CSV/ZIP), and Security Master data (CSV)

import React, { useState } from "react";
import Papa from "papaparse";
import { strFromU8, unzipSync } from "fflate";
import SectionCard from "components/SectionCard";
import { DropdownSelect, FileBrowseInput } from "components/InputStyled";
import { supabase } from "services/supabaseClient";
import { logAudit } from "lib/audit";
import { useProfile } from "context/ProfileContext";
import DebtUploader from "pages/AdminPanel/IssueUploader";
import SecurityMasterUploader from "pages/AdminPanel/SecurityMasterUploader";
import CustodianDataUploader from "pages/AdminPanel/CustodianDataUploader";
import {
  toISO,
  toNullableFloat,
  toZeroDefaultFloat,
  cleanText,
  chunk,
} from "lib/uploadHelpers";

/* ─────────────────────────────── Constants ─────────────────────────────── */

const TRADE_KEYS = [
  "trade_id",
  "user_id",
  "ticker",
  "trade_date",
  "effective_date",
  "termination_date",
  "notional_units",
  "trade_type",
  "leg1_party",
  "leg1_pay",
  "leg1_rate",
  "leg1_leverage",
  "leg1_spread",
  "leg1_strike",
  "leg1_daycount",
  "leg1_payfreq",
  "leg1_paydate",
  "leg1_index",
  "leg2_party",
  "leg2_pay",
  "leg2_rate",
  "leg2_leverage",
  "leg2_spread",
  "leg2_strike",
  "leg2_daycount",
  "leg2_payfreq",
  "leg2_paydate",
  "leg2_index",
];

const VAL_KEYS = [
  "trade_id",
  "valuation_date",
  "notional_amount",
  "clean_value",
  "accrued",
  "total_value",
  "pv01",
  "user_id",
];

const BATCH_SIZE = 1000;

const TRADE_REQUIRED_HEADERS = ["trade_id", "trade_date", "effective_date"];
const MARKET_REQUIRED_HEADERS = ["date", "security_id", "param"];
const SLGS_HISTORICAL_MODE = "slgs_historical";
const SLGS_TIME_MAX_MONTHS = 60;

/* ─────────────────────────────── Helpers ─────────────────────────────── */

function validateHeaders(parsedHeaders, mode) {
  const headers = parsedHeaders.map((h) =>
    String(h || "").toLowerCase().trim()
  );
  if (mode === "trade_data") {
    return TRADE_REQUIRED_HEADERS.every((h) => headers.includes(h));
  }
  if (mode === "market_data") {
    return MARKET_REQUIRED_HEADERS.every((h) => headers.includes(h));
  }
  return false;
}

const toTradeRow = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([k]) => TRADE_KEYS.includes(k))
  );

const toValRow = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => VAL_KEYS.includes(k)));

const normalizePayType = (val) => {
  const v = String(val ?? "").trim().toLowerCase();
  return v === "fixed" || v === "float" ? v : null;
};

const normalizeCsvHeader = (header) =>
  String(header || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const formatSLGSTenor = (s) => {
  const [yy, mm] = String(s || "")
    .split("-")
    .map((part) => Number(part));
  if (!yy && mm) return `${mm}m`;
  if (yy && mm) return `${yy}y${mm}m`;
  return `${yy || 0}y0m`;
};

const slgsMonthsFromYearMonth = (s) => {
  const [yy, mm] = String(s || "")
    .split("-")
    .map((part) => Number(part));

  if (!Number.isFinite(yy) || !Number.isFinite(mm)) return null;
  return yy * 12 + mm;
};

const slgsNameForTenor = (tenor) => {
  const y = parseInt(tenor.match(/(\d+)y/)?.[1] || "0", 10);
  const m = parseInt(tenor.match(/(\d+)m/)?.[1] || "0", 10);
  const totalMonths = y * 12 + m;
  return totalMonths <= 11
    ? `SLGS Certificate ${tenor}`
    : `SLGS Note ${tenor}`;
};

const slgsTimeSecurityId = (fromYearMonth) => {
  const cleanId = String(fromYearMonth || "").replace(/\D/g, "");
  return `slgs-time-${cleanId.padStart(4, "0")}`;
};

const parseCsvText = (text, sourceName) => {
  const result = Papa.parse(text, {
    header: true,
    skipEmptyLines: true,
    delimiter: ",",
    transformHeader: normalizeCsvHeader,
  });

  if (result.errors?.length) {
    throw new Error(`${sourceName}: ${result.errors[0].message}`);
  }

  return {
    data: result.data || [],
    headers: result.meta?.fields || Object.keys(result.data?.[0] || {}),
  };
};

const isDemandDepositCsv = (headers) =>
  ["record_date", "annualized_effective_rate", "daily_factor"].every((h) =>
    headers.includes(h)
  );

const isTimeDepositCsv = (headers) =>
  ["record_date", "from", "through", "rate"].every((h) => headers.includes(h));

const slgsRowsFromDemandDepositCsv = (data) => {
  const rows = [];

  for (const row of data) {
    const date = toISO(row.record_date);
    if (!date) continue;

    const rate = String(row.annualized_effective_rate ?? "").trim();
    if (rate) {
      rows.push({
        date,
        security_id: "slgs-dd-rate",
        ticker: null,
        param: null,
        name: "SLGS Demand Deposit Rate",
        asset_class: "rates",
        tenor: "1d",
        value: toZeroDefaultFloat(rate),
        units: "%",
        source: "US Treasury",
      });
    }

    const factor = String(row.daily_factor ?? "").trim();
    if (factor) {
      rows.push({
        date,
        security_id: "slgs-dd-factor",
        ticker: null,
        param: null,
        name: "SLGS Demand Deposit Factor",
        asset_class: "rates",
        tenor: "1d",
        value: toZeroDefaultFloat(factor),
        units: "",
        source: "US Treasury",
      });
    }
  }

  return rows;
};

const slgsRowsFromTimeDepositCsv = (data) => {
  const rows = [];

  for (const row of data) {
    const date = toISO(row.record_date);
    const from = String(row.from ?? "").trim();
    const rate = String(row.rate ?? "").trim();
    if (!date || !from || !rate) continue;

    const months = slgsMonthsFromYearMonth(from);
    if (months == null || months > SLGS_TIME_MAX_MONTHS) continue;

    const tenor = formatSLGSTenor(from);

    rows.push({
      date,
      security_id: slgsTimeSecurityId(from),
      ticker: null,
      param: null,
      name: slgsNameForTenor(tenor),
      asset_class: "rates",
      tenor,
      value: toZeroDefaultFloat(rate),
      units: "%",
      source: "US Treasury",
    });
  }

  return rows;
};

/* ─────────────────────────────── Component ─────────────────────────────── */

export default function DataUploader() {
  const { profile, loading: profileLoading } = useProfile();

  const isPlatformAdmin = !!profile?.is_admin;
  const userId = profile?.id ?? null;

  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("market_data");
  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState([]);

  if (profileLoading) {
    return <div className="p-4 text-muted">Loading…</div>;
  }

  if (!isPlatformAdmin) {
    return <div className="p-4 text-app">Platform admin access required.</div>;
  }

  const handleFileChange = (selectedFile) => {
    setFile(selectedFile);
    setStatus("");
    setPreview([]);
  };

  const handleModeChange = (value) => {
    setMode(value);
    setFile(null);
    setStatus("");
    setPreview([]);
  };

  const fileAccept =
    mode === "slgs_xml"
      ? ".xml"
      : mode === SLGS_HISTORICAL_MODE
      ? ".zip"
      : ".csv";

  const parseSLGSXML = async (text) => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, "application/xml");

    const demandNode = xml.querySelector("DemandDepositRates");
    const timeNodes = xml.querySelectorAll("TimeDepositRates");
    const rows = [];

    if (demandNode) {
      const date = demandNode.querySelector("Date")?.textContent?.trim();
      const rate = demandNode
        .querySelector("AnnualizedEffectiveRate")
        ?.textContent?.trim();
      const factor = demandNode.querySelector("DailyFactor")?.textContent?.trim();

      if (date && rate) {
        rows.push({
          date: toISO(date),
          security_id: "slgs-dd-rate",
          ticker: null,
          param: null,
          name: "SLGS Demand Deposit Rate",
          asset_class: "rates",
          tenor: "1d",
          value: toZeroDefaultFloat(rate),
          units: "%",
          source: "US Treasury",
        });
      }

      if (date && factor) {
        rows.push({
          date: toISO(date),
          security_id: "slgs-dd-factor",
          ticker: null,
          param: null,
          name: "SLGS Demand Deposit Factor",
          asset_class: "rates",
          tenor: "1d",
          value: toZeroDefaultFloat(factor),
          units: "",
          source: "US Treasury",
        });
      }
    }

    timeNodes.forEach((node) => {
      const date = node.querySelector("Date")?.textContent?.trim();
      const from = node.querySelector("FromYearMonth")?.textContent?.trim();
      const rate = node.querySelector("Rate")?.textContent?.trim();
      if (!date || !from || !rate) return;

      const months = slgsMonthsFromYearMonth(from);
      if (months == null || months > SLGS_TIME_MAX_MONTHS) return;

      const tenor = formatSLGSTenor(from);

      rows.push({
        date: toISO(date),
        security_id: slgsTimeSecurityId(from),
        ticker: null,
        param: null,
        name: slgsNameForTenor(tenor),
        asset_class: "rates",
        tenor,
        value: toZeroDefaultFloat(rate),
        units: "%",
        source: "US Treasury",
      });
    });

    return rows;
  };

  const parseSLGSHistoricalZip = async (zipFile) => {
    if (!/\.zip$/i.test(zipFile.name || "")) {
      throw new Error("SLGS Historical Rates requires the Treasury ZIP file.");
    }

    const archive = unzipSync(new Uint8Array(await zipFile.arrayBuffer()));
    const rows = [];
    const summary = {
      csvFiles: 0,
      demandFiles: 0,
      timeFiles: 0,
      ignoredFiles: 0,
    };

    for (const [name, bytes] of Object.entries(archive)) {
      if (!/\.csv$/i.test(name)) {
        summary.ignoredFiles += 1;
        continue;
      }

      summary.csvFiles += 1;
      const csvText = strFromU8(bytes);
      const { data, headers } = parseCsvText(csvText, name);

      if (isDemandDepositCsv(headers)) {
        summary.demandFiles += 1;
        rows.push(...slgsRowsFromDemandDepositCsv(data));
        continue;
      }

      if (isTimeDepositCsv(headers)) {
        summary.timeFiles += 1;
        rows.push(...slgsRowsFromTimeDepositCsv(data));
        continue;
      }

      summary.ignoredFiles += 1;
    }

    if (!summary.csvFiles) {
      throw new Error("No CSV files were found in the Treasury ZIP file.");
    }

    if (!summary.demandFiles || !summary.timeFiles) {
      throw new Error(
        "The Treasury ZIP must include both Demand Deposit Rate and Time Deposit Rate CSV files."
      );
    }

    return { rows, summary };
  };

  const parseAndUpload = async () => {
    if (!file) return setStatus("❌ Choose a file first.");
    if (!userId) return setStatus("❌ You must be signed in.");
    setStatus("Parsing…");

    const actionForMode =
      mode === "trade_data"
        ? "trade_upload"
        : mode === "market_data"
        ? "market_upload"
        : mode === SLGS_HISTORICAL_MODE
        ? "slgs_historical_upload"
        : "slgs_xml_upload";

    try {
      if (mode === "slgs_xml" || mode === SLGS_HISTORICAL_MODE) {
        const parsed =
          mode === "slgs_xml"
            ? { rows: await parseSLGSXML(await file.text()), summary: null }
            : await parseSLGSHistoricalZip(file);
        const { rows, summary } = parsed;

        if (!rows.length) {
          setStatus("❌ No usable rows found.");
          return;
        }

        const mdMap = new Map();
        for (const r of rows) mdMap.set(`${r.date}__${r.security_id}`, r);
        const rowsDeduped = Array.from(mdMap.values());

        setPreview(rowsDeduped.slice(0, 5));
        setStatus(`Uploading ${rowsDeduped.length} SLGS rate row(s)…`);

        for (const batch of chunk(rowsDeduped, BATCH_SIZE)) {
          const { error } = await supabase
            .from("market_data")
            .upsert(batch, { onConflict: ["date", "security_id"] });
          if (error) throw new Error(`SLGS rates: ${error.message}`);
        }

        await logAudit({
          category: "upload",
          action: actionForMode,
          success: true,
          detail: { fileName: file.name, rows: rowsDeduped.length, summary },
        });

        setStatus(
          mode === SLGS_HISTORICAL_MODE && summary
            ? `✅ Upload complete. SLGS historical rows: ${rowsDeduped.length}\n` +
                `Demand CSV files: ${summary.demandFiles}\n` +
                `Time CSV files: ${summary.timeFiles}`
            : `✅ Upload complete. SLGS XML rows: ${rowsDeduped.length}`
        );
        return;
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        delimiter: ",",
        complete: async ({ data, errors, meta }) => {
          try {
            if (errors?.length) {
              setStatus(`❌ CSV parse error: ${errors[0].message}`);
              return;
            }

            const parsedHeaders = meta.fields || Object.keys(data[0] || {});
            if (!validateHeaders(parsedHeaders, mode)) {
              setStatus(
                `❌ This file does not look like a valid ${mode.replace("_", " ")} CSV.`
              );
              return;
            }

            if (mode === "trade_data") {
              const trades = [];
              const valuations = [];

              let legacyIndexRemaps = 0;

              for (const row of data) {
                const trade_id = String(row.trade_id || "").trim();
                if (!trade_id) continue;

                const leg2_index = cleanText(row.leg2_index ?? row.index);
                if (!row.leg2_index && row.index) legacyIndexRemaps++;

                const tradeObj = {
                  ...row,
                  trade_id,
                  user_id: userId,
                  trade_date: toISO(row.trade_date),
                  effective_date: toISO(row.effective_date),
                  termination_date: toISO(row.termination_date),

                  leg1_pay: normalizePayType(row.leg1_pay),
                  leg2_pay: normalizePayType(row.leg2_pay),

                  leg1_paydate: cleanText(row.leg1_paydate),
                  leg2_paydate: cleanText(row.leg2_paydate),

                  leg1_rate: toNullableFloat(row.leg1_rate),
                  leg1_leverage: toNullableFloat(row.leg1_leverage),
                  leg1_spread: toNullableFloat(row.leg1_spread),
                  leg1_strike: toNullableFloat(row.leg1_strike),

                  leg2_rate: toNullableFloat(row.leg2_rate),
                  leg2_leverage: toNullableFloat(row.leg2_leverage),
                  leg2_spread: toNullableFloat(row.leg2_spread),
                  leg2_strike: toNullableFloat(row.leg2_strike),

                  leg1_index: cleanText(row.leg1_index),
                  leg2_index,
                };

                trades.push(toTradeRow(tradeObj));

                if (row.valuation_date) {
                  const isoDate = toISO(row.valuation_date);
                  if (isoDate && String(isoDate).trim()) {
                    valuations.push(
                      toValRow({
                        trade_id,
                        user_id: userId,
                        valuation_date: isoDate,
                        notional_amount: toZeroDefaultFloat(
                          row.notional_amount || row.notional_units
                        ),
                        clean_value: toZeroDefaultFloat(row.clean_value),
                        accrued: toZeroDefaultFloat(row.accrued),
                        total_value: toZeroDefaultFloat(row.total_value),
                        pv01: toZeroDefaultFloat(row.pv01),
                      })
                    );
                  }
                }
              }

              if (!trades.length && !valuations.length) {
                setStatus("❌ No usable rows found.");
                return;
              }

              setPreview(data.slice(0, 5));
              setStatus(
                `Uploading ${trades.length} trade(s) and ${valuations.length} valuation row(s)…`
              );

              const tradeMap = new Map();
              for (const t of trades) tradeMap.set(t.trade_id, t);
              const tradesDeduped = Array.from(tradeMap.values());

              const valMap = new Map();
              const skipped = [];
              const insertedKeys = [];

              for (const v of valuations) {
                const key = `${v.valuation_date}__${v.trade_id}`;
                if (valMap.has(key)) {
                  skipped.push(v.trade_id);
                } else {
                  insertedKeys.push(key);
                  valMap.set(key, v);
                }
              }
              const valuationsDeduped = Array.from(valMap.values());

              for (const batch of chunk(tradesDeduped, BATCH_SIZE)) {
                const { error } = await supabase
                  .from("trades")
                  .upsert(batch, { onConflict: ["trade_id"] });
                if (error) throw new Error(`Trades: ${error.message}`);
              }

              for (const batch of chunk(valuationsDeduped, BATCH_SIZE)) {
                const { error } = await supabase
                  .from("trade_valuations")
                  .upsert(batch, { onConflict: ["valuation_date", "trade_id"] });
                if (error) throw new Error(`Valuations: ${error.message}`);
              }

              const distinctTradeIds = new Set(
                valuationsDeduped.map((v) => v.trade_id)
              );
              const notImported = skipped.length;
              const skippedTrades = [...new Set(skipped)];

              await logAudit({
                category: "upload",
                action: actionForMode,
                success: true,
                detail: {
                  fileName: file.name,
                  trades: tradesDeduped.length,
                  valuations: valuationsDeduped.length,
                  skipped: skipped.length,
                  inserted: insertedKeys.length,
                  legacyIndexRemaps,
                },
              });

              setStatus(
                `✅ Upload complete.\n` +
                  `Trades: ${tradesDeduped.length}\n` +
                  `Valuations: ${valuationsDeduped.length} rows across ${distinctTradeIds.size} trade(s)\n` +
                  `↪️ ${insertedKeys.length} row(s) processed (upsert overwrites existing keys)\n` +
                  (legacyIndexRemaps
                    ? `ℹ️ Remapped ${legacyIndexRemaps} legacy "index" field(s) to leg2_index\n`
                    : "") +
                  (notImported > 0
                    ? `⚠️ ${notImported} valuation row(s) NOT IMPORTED. Affected trade_id(s): ${skippedTrades.join(
                        ", "
                      )}`
                    : `All valuation rows imported.`)
              );

              return;
            }

            if (mode === "market_data") {
              const rows = [];

              for (const row of data) {
                if (!row.security_id || !row.date) continue;
                const val = String(row.value ?? "").trim();
                if (!val || val === "#N/A N/A") continue;

                rows.push({
                  date: toISO(row.date),
                  security_id: row.security_id,
                  ticker: row.ticker,
                  param: row.param,
                  name: row.name,
                  asset_class: row.asset_class,
                  tenor: row.tenor,
                  value: toZeroDefaultFloat(row.value),
                  units: row.units,
                  source: row.source,
                });
              }

              if (!rows.length) {
                setStatus("❌ No usable rows found.");
                return;
              }

              const mdMap = new Map();
              for (const r of rows) mdMap.set(`${r.date}__${r.security_id}`, r);
              const rowsDeduped = Array.from(mdMap.values());

              setPreview(rowsDeduped.slice(0, 5));
              setStatus(`Uploading ${rowsDeduped.length} market data row(s)…`);

              for (const batch of chunk(rowsDeduped, BATCH_SIZE)) {
                const { error } = await supabase
                  .from("market_data")
                  .upsert(batch, { onConflict: ["date", "security_id"] });
                if (error) throw new Error(`Market data: ${error.message}`);
              }

              await logAudit({
                category: "upload",
                action: actionForMode,
                success: true,
                detail: { fileName: file.name, rows: rowsDeduped.length },
              });

              setStatus(`✅ Upload complete. Market data rows: ${rowsDeduped.length}`);
              return;
            }
          } catch (e) {
            await logAudit({
              category: "upload",
              action: actionForMode,
              success: false,
              detail: { fileName: file?.name, error: e.message },
            });
            setStatus(`❌ Upload error: ${e.message}`);
          }
        },
        error: (err) => setStatus(`CSV parse error: ${err.message}`),
      });
    } catch (e) {
      await logAudit({
        category: "upload",
        action: actionForMode,
        success: false,
        detail: { fileName: file?.name, error: e.message },
      });
      setStatus(`❌ Upload error: ${e.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Upload Market Data">
        <div className="flex flex-wrap items-end gap-3">
          <DropdownSelect
            label="Upload Type"
            value={mode}
            onChange={handleModeChange}
            options={[
              { value: "market_data", label: "Market Data (CSV)" },
              { value: "trade_data", label: "Trade Data (CSV)" },
              { value: "slgs_xml", label: "SLGS Daily Rates (XML)" },
              {
                value: SLGS_HISTORICAL_MODE,
                label: "SLGS Historical Rates (CSV/ZIP)",
              },
            ]}
            placeholder=""
            className="min-w-72"
          />

          <FileBrowseInput
            label="Upload File"
            file={file}
            accept={fileAccept}
            placeholder={
              mode === "slgs_xml"
                ? "Select an XML file"
                : mode === SLGS_HISTORICAL_MODE
                  ? "Select a ZIP file"
                  : "Select a CSV file"
            }
            onFileChange={handleFileChange}
            inputKey={mode}
            className="min-w-96"
          />

          {mode === "slgs_xml" && (
            <a
              href="https://treasurydirect.gov/GA-SL/SLGS/selectSLGSDate.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm linkCls"
            >
              Go to TreasuryDirect SLGS Site
            </a>
          )}

          {mode === SLGS_HISTORICAL_MODE && (
            <a
              href="https://fiscaldata.treasury.gov/datasets/slgs-daily-rate-table/demand-deposit-rate"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm linkCls"
            >
              Go to Treasury FiscalData Site
            </a>
          )}
        </div>

        <button
          onClick={parseAndUpload}
          className="btn btn-primary mt-3 mb-4 disabled:opacity-50"
          disabled={!file}
        >
          Upload
        </button>

        {status && <p className="text-sm whitespace-pre-line mb-3">{status}</p>}
        {preview.length > 0 && (
          <div className="text-xs text-muted">
            <p className="font-semibold">Preview:</p>
            <pre className="overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(preview, null, 2)}
            </pre>
          </div>
        )}
      </SectionCard>

      <CustodianDataUploader />
      <SecurityMasterUploader />
      <DebtUploader />
    </div>
  );
}
